"use client";

import {
  Background,
  Button,
  Column,
  Feedback,
  Grid,
  Heading,
  Icon,
  type IconName,
  Line,
  Row,
  StatusIndicator,
  type StatusIndicatorColor,
  Text,
} from "@once-ui-system/core";
import { useState } from "react";
import { app } from "@/resources/app";

const CAPABILITIES: { color: StatusIndicatorColor; title: string; body: string }[] = [
  { color: "green", title: "Voicebots", body: "Versioned, publishable bot logic per campaign" },
  { color: "blue", title: "Campaigns & live agents", body: "Leads, routing and manual agents in one place" },
  { color: "indigo", title: "Governance", body: "Role-based access on every API, permanent audit trail" },
];

export function Brand({ onSolid }: { onSolid?: boolean }) {
  return (
    <Row gap="12" vertical="center">
      <Row solid="brand-strong" radius="m" width="32" height="32" center>
        <Icon name="security" size="s" onSolid="brand-strong" />
      </Row>
      <Text variant="heading-strong-m" onBackground={onSolid ? "neutral-strong" : undefined}>
        {app.name}
      </Text>
    </Row>
  );
}

/** Split auth layout: product panel on the left, a narrow form column on the right. */
export function AuthFrame({ topAction, children }: { topAction?: React.ReactNode; children: React.ReactNode }) {
  return (
    <Grid as="main" columns="2" fillWidth minHeight="100vh" m={{ columns: 1 }}>
      <Column theme="dark" background="page" padding="40" vertical="between" overflow="hidden" m={{ hide: true }}>
        <Background
          position="absolute"
          top="0"
          left="0"
          fill
          pointerEvents="none"
          gradient={{
            display: true,
            colorStart: "brand-alpha-strong",
            colorEnd: "static-transparent",
            x: 0,
            y: 100,
            width: 140,
            height: 90,
            opacity: 60,
          }}
          dots={{ display: true, color: "neutral-alpha-medium", size: "24", opacity: 30 }}
        />
        <Row zIndex={1}>
          <Brand onSolid />
        </Row>
        <Column zIndex={1} gap="32" maxWidth={32}>
          <Heading as="p" variant="display-strong-xs" wrap="balance">
            Run every customer conversation, from voicebot to live agent, on one governed console.
          </Heading>
          <Column background="surface" border radius="l" overflow="hidden">
            {CAPABILITIES.map((c, i) => (
              <Column key={c.title}>
                {i > 0 && <Line />}
                <Row paddingX="20" paddingY="16" gap="16" vertical="center">
                  <StatusIndicator color={c.color} size="s" ariaLabel={c.title} />
                  <Column gap="2">
                    <Text variant="label-strong-s">{c.title}</Text>
                    <Text variant="body-default-xs" onBackground="neutral-weak">
                      {c.body}
                    </Text>
                  </Column>
                </Row>
              </Column>
            ))}
          </Column>
        </Column>
      </Column>

      <Column padding="32" s={{ padding: "20" }}>
        <Row fillWidth horizontal="between" vertical="center">
          <Row hide m={{ hide: false }}>
            <Brand />
          </Row>
          <Row flex={1} horizontal="end">
            {topAction}
          </Row>
        </Row>
        <Column flex={1} center paddingY="40">
          <Column maxWidth={24} gap="24">
            {children}
          </Column>
        </Column>
        <Text variant="body-default-xs" onBackground="neutral-weak" align="center">
          Access is role-based and every sign-in is recorded in your organization's audit log.
        </Text>
      </Column>
    </Grid>
  );
}

const PROVIDER_ICON: Record<string, IconName> = { google: "google", microsoft: "microsoft", keycloak: "lock" };

export type PlatformProvider = { id: string; name: string; configured: boolean };

/** Google / Microsoft buttons, configured once for the whole platform by the operator. */
export function SocialButtons({
  providers,
  intent,
  next,
}: {
  providers: PlatformProvider[];
  intent: "login" | "signup";
  next?: string;
}) {
  const [notice, setNotice] = useState<string | null>(null);
  if (providers.length === 0) return null;
  const verb = intent === "signup" ? "Sign up with" : "Continue with";
  return (
    <Column gap="12">
      <Column gap="8">
        {providers.map((p) =>
          p.configured ? (
            <Button
              key={p.id}
              fillWidth
              variant="secondary"
              prefixIcon={PROVIDER_ICON[p.id] ?? "enter"}
              href={`/auth/sso/${p.id}?intent=${intent}&next=${encodeURIComponent(next ?? "/dashboard")}`}
            >
              {verb} {p.name}
            </Button>
          ) : (
            <Button
              key={p.id}
              fillWidth
              variant="secondary"
              prefixIcon={PROVIDER_ICON[p.id] ?? "enter"}
              onClick={() =>
                setNotice(
                  `${p.name} sign-in isn't connected yet. Set ${p.id.toUpperCase()}_CLIENT_ID and ${p.id.toUpperCase()}_CLIENT_SECRET in the backend .env and restart it.`,
                )
              }
            >
              {verb} {p.name}
            </Button>
          ),
        )}
      </Column>
      {notice && <Feedback variant="warning" description={notice} />}
      <Row vertical="center" gap="12">
        <Line flex={1} />
        <Text variant="label-default-xs" onBackground="neutral-weak">
          OR CONTINUE WITH EMAIL
        </Text>
        <Line flex={1} />
      </Row>
    </Column>
  );
}

/** A labelled field: label above the input so browser autofill never overlaps a floating label. */
export function FieldLabel({ htmlFor, children, aside }: { htmlFor: string; children: React.ReactNode; aside?: React.ReactNode }) {
  return (
    <Row horizontal="between" vertical="center">
      <label htmlFor={htmlFor}>
        <Text variant="label-strong-s">{children}</Text>
      </label>
      {aside}
    </Row>
  );
}
